import React from 'react';

import Job from './job';

import { graphql } from 'gatsby';
import dayjs from 'dayjs';
import {
  excludedSpecialties,
  stateOptions,
  specialtyOptions,
  jobTypes,
  positionCodeToDisplayName,
} from '../../utils/displayHelper';
import GatsbyHead from '../../components/common/GatsbyHead';

const StaticJob = (props) => {
  return (
    <Job
      job={props.data.job}
      indexCity={props.pageContext.indexCity}
      indexMsa={props.pageContext.indexMsa}
      isDynamic={false}
    />
  );
};

export default StaticJob;

export const pageQuery = graphql`
  query ($id: String!) {
    job: jobPostingsCsv(jobId: { eq: $id }) {
      city
      createdDate
      description
      dow
      expireDate
      externalUrl
      isConfidential
      isNational
      jobDates
      jobId
      lat
      lng
      msa
      name
      occupationalCategory
      position
      source
      specialty
      state
      stateAbbrev
      title
      jobType
      updatedDate
      uri
      zip
    }
  }
`;

export function Head({ data }) {
  const { job } = data;
  const updatedPosition = `${
    'specialty' in job && !excludedSpecialties.includes(job.specialty)
      ? `${specialtyOptions[job.specialty] || job.specialty}`
      : ''
  } ${positionCodeToDisplayName[job.position]}`;
  const state = 'jobId' in job ? job.state : stateOptions[job.state];

  const isConfidential =
    'isConfidential' in job ? job.isConfidential === 'true' : false;

  const createdDate = 'createdDate' in job ? job.createdDate : job.postedAt;

  const salaryIsDollarAmt =
    'salaryUnits' in job &&
    (job.salaryUnits?.startsWith('HOUR') ||
      job.salaryUnits?.startsWith('YEAR'));

  let schemaOrgData = {
    '@context': 'http://schema.org',
    '@id': `https://www.dentalpost.net/dental-jobs${job.uri}`,
    '@type': 'JobPosting',
    datePosted: dayjs(createdDate).format('YYYY-MM-DD'),
    description: job.description,
    employmentType: job.jobType === 'PLANNED' ? 'temporary' : 'full-time',
    industry: 'Offices of Dentists',
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressCountry: 'US',
        addressLocality: job.city,
        addressRegion: state,
        postalCode: job.zip,
      },
    },
    baseSalary: !!job.salary
      ? salaryIsDollarAmt
        ? {
            currency: 'USD',
            '@type': 'MonetaryAmount',
            value: job.salaryUnits.endsWith('RANGE') ? undefined : job.salary,
            minValue: job.salaryUnits.endsWith('Range')
              ? job.salary.split(' ')[0]
              : undefined,
            maxValue: job.salaryUnits.endsWith('Range')
              ? job.salary.split(' ')[2]
              : undefined,
            unitText: `per ${job.salaryUnits.replace('RANGE', '').toLowerCase()}`,
          }
        : {
            '@type': 'Number',
            value: job.salaryUnits.endsWith('RANGE') ? undefined : job.salary,
            minValue: job.salaryUnits.endsWith('Range')
              ? job.salary.split(' ')[0]
              : undefined,
            maxValue: job.salaryUnits.endsWith('Range')
              ? job.salary.split(' ')[2]
              : undefined,
            unitText: `% of ${job.salaryUnits.toLowerCase()}`,
          }
      : undefined,
    occupationalCategory: job.occupationalCategory,
    title: updatedPosition,
    hiringOrganization: {
      '@type': 'Organization',
      name: isConfidential ? 'Confidential Employer' : job.name,
    },
    workHours: job.dow,
    validThrough: job.expireDate,
  };

  return (
    <GatsbyHead
      title={`${
        job.jobType === 'PLANNED' ? 'Temporary' : 'Permanent'
      } ${updatedPosition} Job in ${job.city}, ${state} | ${job.jobId}`}
      description={`Newly listed! A ${
        jobTypes[job.jobType]
      } ${updatedPosition} Job in ${
        job.city
      }, ${state}. Apply here today with DentalPost! It's as easy as 1 click to apply! | ${
        job.jobId
      }`}
    >
      {!isConfidential && dayjs().isBefore(dayjs(job.expireDate)) && (
        <script type="application/ld+json">
          {JSON.stringify(schemaOrgData)}
        </script>
      )}
    </GatsbyHead>
  );
}
